import {type IssueField, IssueFieldPicker} from '@github-ui/item-picker/IssueFieldPicker'
import {
  IssueSingleSelectFieldPicker,
  type IssueFieldSingleSelectOption,
} from '@github-ui/item-picker/IssueSingleSelectFieldPicker'
import {useCallback, useMemo, useState, type Dispatch, type SetStateAction} from 'react'
import {AddIssueFieldButton} from './AddIssueFieldButton'
import {isSingleSelectField, isTextField} from '../../utils/issue-field-helper'

type FieldSelectorProps = {
  owner: string
  fieldsSet: string[]
  setFieldsSet: Dispatch<SetStateAction<string[]>>
  onIssueFieldSelected: (selectedField: IssueField, selectedOption?: IssueFieldSingleSelectOption | null) => void
  shortcutEnabled: boolean
}

export function FieldsSelector({
  owner,
  fieldsSet,
  setFieldsSet,
  onIssueFieldSelected,
  shortcutEnabled,
}: FieldSelectorProps) {
  const [activeSingleSelectField, setActiveSingleSelectField] = useState<IssueField | null>(null)

  const anchorElement = useMemo(() => {
    // eslint-disable-next-line react/display-name
    return (anchorProps: React.HTMLAttributes<HTMLElement>, ref?: React.Ref<HTMLButtonElement>) => (
      <AddIssueFieldButton anchorProps={anchorProps} ref={ref} />
    )
  }, [])

  const onIssueFieldSelectedInternal = useCallback(
    async (selectedFields: IssueField[]) => {
      if (selectedFields.length > 0 && selectedFields[0]?.id) {
        const field = selectedFields[0]
        if (isSingleSelectField(field)) {
          // for single select field, we need to show the picker to select an option
          setActiveSingleSelectField(field)
        } else if (isTextField(field)) {
          onIssueFieldSelected(field)
        }

        if (field.name) {
          setFieldsSet(prevFieldsSet => {
            const newFieldsSet = [...prevFieldsSet]
            if (field.name && !newFieldsSet.includes(field.name)) {
              newFieldsSet.push(field.name)
            }
            return newFieldsSet
          })
        }
      }
    },
    [onIssueFieldSelected, setFieldsSet],
  )

  const onIssueSingleSelectValueSelected = useCallback(
    (selectedOption: IssueFieldSingleSelectOption | null) => {
      setActiveSingleSelectField(null)
      if (!activeSingleSelectField || !isSingleSelectField(activeSingleSelectField)) return
      onIssueFieldSelected(activeSingleSelectField, selectedOption)
    },
    [activeSingleSelectField, onIssueFieldSelected],
  )

  return (
    <>
      {activeSingleSelectField?.id && (
        <IssueSingleSelectFieldPicker
          fieldId={activeSingleSelectField.id}
          selectedOption={null}
          onSelectionChange={onIssueSingleSelectValueSelected}
          onClose={() => setActiveSingleSelectField(null)}
          readonly={false}
          isLazy={false}
          shortcutEnabled
          anchorElement={anchorElement}
        />
      )}
      <IssueFieldPicker
        owner={owner}
        fieldsSet={fieldsSet}
        onSelectionChange={onIssueFieldSelectedInternal}
        shortcutEnabled={shortcutEnabled}
        anchorElement={anchorElement}
        insidePortal={false}
      />
    </>
  )
}

try{ FieldsSelector.displayName ||= 'FieldsSelector' } catch {}